import { AddIcon } from 'assests/GmsIcons';
import Button from 'Components/DesignSystem/Button/Button';
import Tooltip from 'Components/DesignSystem/Tooltip/Tooltip';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { ChevronDown } from 'Components/DesignSystem/Iconography/Icons';
import { FOCUS_RING_INSET } from 'Components/DesignSystem/focusRing';
import { EXPLORER_ROW, EXPLORER_ROW_STATE, EXPLORER_SELECT_BTN } from './explorerRowChrome';
import React, { useEffect, useState, useContext } from 'react';
import { useLocation } from 'react-router';
import { AppContext } from 'Context/AppContext';
import { CATEGORY_NAME, CREATE_CATEGORY, PRODUCT_CATEGORY, SUB_MODEL, FIELD, CONNECTOR, CONNECTOR_NAME, CREATE_CONNECTOR, SAVE_IN_PROGRESS_MESSAGE, DELETE, DELETE_SUBMODEL_TITLE, DELETE_SUBMODEL_MESSAGE } from 'Pages/GMS/ISR/ISRConstants';
import DeleteDataModel from './DeleteDataModel';
import CreateFieldDialog from 'Pages/GMS/ISR/Components/CreateFieldDialog';
import { TConnector, TField, TIndexState, TProductCategory, TSubmodel, TUpsertISRProjectDataModelsInput, TTransformation } from '../model';
import FieldItem from './FieldItem';
import CreateISRDialog from 'Pages/GMS/ISR/Components/CreateISRDialog';
import ProductCategoryItem from './ProductCategoryItem';
import { useISRContext } from 'Pages/GMS/ISR/Hooks/useISRContext';
import ConfirmDialog from "../../../Components/ConfirmDialog";
import DSConfirmDialog from 'Components/DesignSystem/ConfirmDialog/ConfirmDialog';
import { isItemSelected, pickNextSiblingIndex } from './submodelDeleteHelpers';
import { toast } from 'Components/DesignSystem/Toast/useToast';

type TSubModelProps = {
    id: string;
    submodel: TSubmodel;
    searchInput: string;
    isSelected: boolean;
    classColor?: string;
    collapseAll: boolean;
    onSelect: () => void;
    submodelIndex: number;
    indexInfo: TIndexState;
    highlightedName?: React.ReactNode;
    onDeleteSubmodel: (submodelName: string|null|undefined) => void;
    userProjectDataModels: TUpsertISRProjectDataModelsInput;
    setIndex: React.Dispatch<React.SetStateAction<TIndexState>>;
    selectedItem: TField | TSubmodel | TProductCategory | TConnector | null ;
    setSelectedItem: React.Dispatch<React.SetStateAction<TField | TSubmodel | TProductCategory | TConnector | null >>;
    setUserProjectDataModels: React.Dispatch<React.SetStateAction<TUpsertISRProjectDataModelsInput>>;
    setSubSelectedItem: React.Dispatch<React.SetStateAction<TTransformation | null >>;
}

const SubModelItem = ({id, searchInput, submodel, onDeleteSubmodel, selectedItem, setSubSelectedItem, setIndex, submodelIndex, setSelectedItem, setUserProjectDataModels, isSelected, onSelect, userProjectDataModels, indexInfo, highlightedName, classColor, collapseAll}: TSubModelProps) => {
    const isrContext = useISRContext();
    const location = useLocation();
    const isGuide = location.pathname.includes('/guide/');
    const { enableConfigure } = useContext(AppContext);
    const isConfigureDisabled = isGuide && !enableConfigure;
    const [collapse, setCollapse] = useState(false);
    const [showConnectorTooltip, setShowConnectorTooltip] = useState<boolean>(false);
    const [showCategoryDialog, setShowCategoryDialog] = useState<boolean>(false);
    const [showConnectorDialog, setShowConnectorDialog] = useState<boolean>(false);
    const [showFieldDialog, setShowFieldDialog] = useState<boolean>(false);
    const [deleteFieldId, setDeleteFieldId] = useState<string|null|undefined>(null);
    const [showDeleteSubmodelConfirm, setShowDeleteSubmodelConfirm] = useState<boolean>(false);
    const openCategoryDialog = () => setShowCategoryDialog(true);
    const closeCategoryDialog = () => setShowCategoryDialog(false);

    const openFieldDialog = () => setShowFieldDialog(true);
    const closeFieldDialog = () => setShowFieldDialog(false);

    const openConnectorDialog = () => setShowConnectorDialog(true);
    const closeConnectorDialog = () => setShowConnectorDialog(false);

    useEffect(() => {
        setCollapse(collapseAll);
    }, [collapseAll]);

    const handleCollapse = () => {
        setCollapse((prev) => prev = !prev);
    }

    // The overflow menu's Delete only ASKS; the deletion runs from the dialog's
    // Delete. Sub-model delete was the one Guide delete with no confirmation.
    const handleContextMenuOptionClick = () => {
        setShowDeleteSubmodelConfirm(true);
    };

    const confirmDeleteSubmodel = () => {
        setShowDeleteSubmodelConfirm(false);
        onDeleteSubmodel(submodel?.id);
    };

    const onFieldSubmit = (fieldName: string, fieldType: string, calculated: boolean, filter: boolean) => {
        isrContext?.setUpdateLocalState(true);
        const newField: TField = { type: fieldType, name: fieldName, belongsToDataModel: true,  dataConfiguration: {enableCalculation: calculated, filterAvailable: filter, minQuantity: 0, maxQuantity: 1, minCount: 0, maxCount: 1}, additionalData: {}, rules: [], };
        updateSubmodelFields(newField);
    };

    const onCategoryFieldSubmit = (fieldName: string) => {
        isrContext?.setUpdateLocalState(true);
        const newField: TProductCategory = { type: 'Category', data: {type: 'Category', name: fieldName, belongsToDataModel: true ,  categories: {type: '', data: []}, transformations:[], compatioScore:false, required: false, minQuantity: 0, maxQuantity: 1, minCount: 0, maxCount: 1} };
        addCategory(newField);
    };

    const onConnectorFieldSubmit = (fieldName: string) => {
        isrContext?.setUpdateLocalState(true);
        const newField: TConnector = { type: "Connector", data: {type: "Connector", isConsumable: true, name: fieldName, belongsToDataModel: true ,  submodels: []} };
        addConnector(newField);
    };

    const addConnector = (connector: TConnector) => {
        setUserProjectDataModels((prevProjectData) => {
            const updatedDataModels = prevProjectData?.project?.dataModels.map((dataModel) => {
                if (dataModel?.data?.name === submodel?.data?.name) {
                    return {...dataModel, data: {...dataModel?.data, connectors: [...dataModel?.data?.connectors ?? [], connector],},};
                }
                return dataModel;
            });
            return {
                ...prevProjectData,
                project: {...prevProjectData?.project, dataModels: updatedDataModels,},
            }
        })
    }


    const addCategory = (newField: TProductCategory) => {
        setUserProjectDataModels((prevProjectData) => {
            const updatedDataModels = prevProjectData.project.dataModels.map((dataModel) => {
                if (dataModel.data.name === submodel?.data?.name) {
                    return { ...dataModel, data: { ...dataModel.data, productCategories: [...dataModel.data.productCategories, newField], }, };
                }
                return dataModel;
            });

            return {
                ...prevProjectData,
                project: { ...prevProjectData.project, dataModels: updatedDataModels, },
            };
        });
    };

    const updateSubmodelFields = (newField: TField) => {
        setUserProjectDataModels((prevProjectData) => {
            const updatedDataModels = prevProjectData.project.dataModels.map((dataModel) => {
                if (dataModel.data.name === submodel?.data?.name) {
                    return { ...dataModel, data: { ...dataModel.data, fields: [...dataModel.data.fields, newField], }, };
                }
                return dataModel;
            });

            return {
                ...prevProjectData,
                project: { ...prevProjectData.project, dataModels: updatedDataModels, },
            };
        });
    };

    // Reset every TIndexState key so SubModelDetails mounts with a consistent
    // indexInfo. Avoids the slot-overwrite bug where a stale dataModelIndex
    // pointed at the wrong submodel after setSelectedItem(null).
    const resetIndexToParentSubmodel = () => {
        setIndex({
            dataModelIndex: submodelIndex,
            fieldIndex: null,
            categoryIndex: null,
            connectorIndex: null,
            fieldOutsideDataModelIndex: null,
            categoryOutsideDataModelIndex: null,
            connectorOutsideDataModelIndex: null,
            TransformationIndex: null,
        });
    };

    const deleteSubmodelFields = (fieldId: string|null|undefined) => {
        if (!fieldId) {
            toast.info(SAVE_IN_PROGRESS_MESSAGE);
            return;
        }
        const deletedIndex = submodel.data.fields.findIndex((field) => field?.id === fieldId);
        const updatedFields = submodel.data.fields.filter((field) => field?.id !== fieldId);
        const updatedSubmodel: TSubmodel = {
            ...submodel,
            data: { ...submodel.data, fields: updatedFields },
        };
        setUserProjectDataModels((prevProjectData) => {
            const updatedDataModels = prevProjectData.project.dataModels.map((dataModel) =>
                dataModel.data.name === submodel?.data?.name ? updatedSubmodel : dataModel
            );
            return { ...prevProjectData, project: { ...prevProjectData.project, dataModels: updatedDataModels, }, };
        });
        // Only retarget selection if the deleted item is the one currently shown
        // in the right pane. Otherwise preserve the user's existing selection.
        if (isItemSelected(selectedItem, fieldId)) {
            const nextIndex = pickNextSiblingIndex(deletedIndex, updatedFields.length);
            if (nextIndex >= 0) {
                setSelectedItem(updatedFields[nextIndex]);
                setIndex({
                    dataModelIndex: submodelIndex,
                    fieldIndex: nextIndex,
                    categoryIndex: null,
                    connectorIndex: null,
                    fieldOutsideDataModelIndex: null,
                    categoryOutsideDataModelIndex: null,
                    connectorOutsideDataModelIndex: null,
                    TransformationIndex: null,
                });
            } else {
                setSelectedItem(updatedSubmodel);
                resetIndexToParentSubmodel();
            }
        }
        isrContext?.setFieldDelete(isrContext?.fieldDelete.filter((e:string) => e !== fieldId).concat([fieldId]));
    };

    const deleteSubmodelConnectors = (fieldId: string|null|undefined) => {
        if (!fieldId) {
            toast.info(SAVE_IN_PROGRESS_MESSAGE);
            return;
        }
        const updatedConnectors = submodel.data.connectors.filter((field) => field?.id !== fieldId);
        const updatedSubmodel: TSubmodel = {
            ...submodel,
            data: { ...submodel.data, connectors: updatedConnectors },
        };
        setUserProjectDataModels((prevProjectData) => {
            const updatedDataModels = prevProjectData.project.dataModels.map((dataModel) =>
                dataModel.data.name === submodel?.data?.name ? updatedSubmodel : dataModel
            );
            return { ...prevProjectData, project: { ...prevProjectData.project, dataModels: updatedDataModels, }, };
        });
        // Only retarget selection if the deleted item is the one currently shown
        // in the right pane. Otherwise preserve the user's existing selection
        // and index so deleting a non-selected sibling doesn't hijack context.
        if (isItemSelected(selectedItem, fieldId)) {
            setSelectedItem(updatedSubmodel);
            resetIndexToParentSubmodel();
        }
    };

    const deleteSubmodelCategories = (fieldId: string|null|undefined) => {
        if (!fieldId) {
            toast.info(SAVE_IN_PROGRESS_MESSAGE);
            return;
        }
        // Category ids bound to the product-category being removed. Used to drop only
        // the field filter rows that referenced them, so deleting a category field
        // doesn't leave orphaned filters pointing at a category no longer in the sub-model.
        const removedCategory = submodel.data.productCategories.find((field) => field?.id === fieldId);
        const removedCategoryIds = (removedCategory?.data?.categories?.data ?? []).map((category) => category.id);
        const updatedCategories = submodel.data.productCategories.filter((field) => field?.id !== fieldId);
        const updatedFields = removedCategoryIds.length > 0
            ? submodel.data.fields.map((field) => {
                const filters = field.dataConfiguration?.multiFilter?.filters;
                if (field.dataConfiguration?.multiFilter && filters && filters.length > 0) {
                    field.dataConfiguration.multiFilter.filters = filters.filter(
                        (f) => !removedCategoryIds.includes(f.categoryId)
                    );
                }
                return field;
            })
            : submodel.data.fields;
        const updatedSubmodel: TSubmodel = {
            ...submodel,
            data: { ...submodel.data, productCategories: updatedCategories, fields: updatedFields },
        };
        setUserProjectDataModels((prevProjectData) => {
            const updatedDataModels = prevProjectData.project.dataModels.map((dataModel) =>
                dataModel.data.name === submodel?.data?.name ? updatedSubmodel : dataModel
            );
            return { ...prevProjectData, project: { ...prevProjectData.project, dataModels: updatedDataModels, }, };
        });
        // Only retarget selection if the deleted item is the one currently shown
        // in the right pane. Otherwise preserve the user's existing selection
        // and index so deleting a non-selected sibling doesn't hijack context.
        if (isItemSelected(selectedItem, fieldId)) {
            setSelectedItem(updatedSubmodel);
            resetIndexToParentSubmodel();
        }
    };

    const deleteSubmodelTransformations = (categoryId: string, transformationId: string) => {
        if (!categoryId || !transformationId) {
            toast.info(SAVE_IN_PROGRESS_MESSAGE);
            return;
        }
        setUserProjectDataModels((prevProjectData) => {
            const updatedDataModels = prevProjectData.project.dataModels.map((dataModel) => {

                if (dataModel.data.name === submodel?.data?.name) {
                    const updatedFields = dataModel.data.productCategories.map(
                        (category) => {
                            if (category.id === categoryId) {
                                category.data.transformations = category?.data?.transformations?.filter(
                                    transformation =>
                                        transformation.id !== transformationId)
                            }
                            return category
                        }
                    );
                    return { ...dataModel, data: { ...dataModel.data, productCategories: updatedFields, }, };
                }
                return dataModel;
            });
            


            return { ...prevProjectData, project: { ...prevProjectData.project, dataModels: updatedDataModels, }, };
        });
    };

    const handleFieldSelect = (dataModelIndex: number, fieldIndex: number) => {
        setIndex({ dataModelIndex, fieldIndex, fieldOutsideDataModelIndex: null });
    };

    const handleCategorySelect = (dataModelIndex: number, categoryIndex: number) => {
        setIndex({ dataModelIndex, fieldIndex: null, categoryIndex: categoryIndex , fieldOutsideDataModelIndex: null });
    };

    const handleConnectorSelect = (dataModelIndex: number, connectorIndex: number) => {
        setIndex({ dataModelIndex, fieldIndex: null, categoryIndex: null, connectorIndex: connectorIndex, fieldOutsideDataModelIndex: null });
    };

    return (
        <React.Fragment key={submodel?.data?.name + submodel?.type}>
            {/*
                `surface-sunken`, was `#f9fbfe` — a raw hex 1.04:1 off white, i.e. a
                card ground nobody could see. `border-edge` and `grey-200` are the
                same #dadbdd, and `rounded-ctl` is the same 4px.
            */}
            <div id={id} className='tw-rounded-ctl tw-bg-surface-sunken tw-box-border tw-w-full tw-h-auto tw-border tw-border-solid tw-border-edge tw-mt-3'>
                <div
                    id={`${id}-header`}
                    data-testid={`${id}-header`}
                    className={`${EXPLORER_ROW} tw-w-full tw-h-auto ${EXPLORER_ROW_STATE(isSelected)}`}
                >
                    <button
                        type="button"
                        id={`${id}-select-btn`}
                        data-testid={`${id}-select-btn`}
                        onClick={onSelect}
                        aria-current={isSelected ? 'true' : undefined}
                        className={`${EXPLORER_SELECT_BTN} ${FOCUS_RING_INSET}`}
                    >
                        <div id={`${id}-title-section`} className='tw-flex tw-justify-start tw-items-stretch tw-w-full'>
                            {classColor ? <div id={`${id}-color-bar`} style={{backgroundColor: classColor}} className="tw-w-[5px] tw-shrink-0 tw-self-stretch tw-rounded-tl-[3px] tw-rounded-bl-[3px]" /> : null}
                            <div id={`${id}-labels`} className='tw-py-[5px] tw-px-3 tw-min-w-0 tw-text-left'>
                                {highlightedName ?
                                    <Typography as="div" variant={isSelected ? 'sub-title-label' : 'sub-body-supporting'} id={`${id}-name-highlighted`} data-testid={`${id}-name-highlighted`} className='tw-break-words'>{highlightedName}</Typography> :
                                    <Typography as="div" variant={isSelected ? 'sub-title-label' : 'sub-body-supporting'} id={`${id}-name`} data-testid={`${id}-name`} className='tw-break-words'>{submodel?.data?.name ?? ""}</Typography>
                                }
                                <Typography as="div" variant="caption-metadata" id={`${id}-type`} data-testid={`${id}-type`} className={isSelected ? '' : 'tw-text-ink-tertiary'}>{SUB_MODEL}</Typography>
                            </div>
                        </div>
                    </button>
                    <div id={`${id}-actions`} className='tw-flex tw-items-center tw-gap-1 tw-pe-2'>
                        {/*
                            No `onTriggerClick`: a sub-model header's ⋮ must NOT select
                            the header. That used to be a `stopPropagation` on this
                            wrapper — the two field/category rows had no such wrapper, so
                            their ⋮ DID select. Both behaviours are preserved; see the
                            prop's note in `DeleteDataModel`.
                        */}
                        <div id={`${id}-delete-menu`} data-testid={`${id}-delete-menu`}><DeleteDataModel id={id} onContextMenuOptionClick={handleContextMenuOptionClick} menuId={`${id}-delete-menu-menu`} deleteButtonId={`${id}-menu-btn-delete`}/></div>
                        {/*
                            The chevron had no accessible name (a <button> whose only
                            child is an SVG), no `aria-expanded`, and no focus ring. It
                            also drew two DIFFERENT glyphs: `UpArrow` measured 14×8 and
                            `DownArrow` 24×24 — the latter baking its own #E5E5EA rect,
                            which is why nobody noticed the ground was being repainted.
                            One DS chevron, rotated.

                            Ground is `edge` (#dadbdd): `gainsboro-100` (#e5e5ea) has
                            no DS token, and `surface-sunken` measured too faint against
                            the header's white — 1.09 where the old chip read at 1.26.
                            The glyph goes `accent` on hover rather than the ground
                            moving, so the chip stays one shape.
                        */}
                        <button
                            type="button"
                            data-testid={`${id}-collapse-btn`}
                            id={`${id}-collapse-btn`}
                            aria-expanded={!collapse}
                            aria-controls={`${id}-body`}
                            aria-label={`${collapse ? 'Expand' : 'Collapse'} ${submodel?.data?.name ?? SUB_MODEL}`}
                            onClick={(e) => { e.stopPropagation(); handleCollapse(); }}
                            className={`tw-shrink-0 tw-rounded-ctl tw-bg-edge tw-text-ink hover:tw-text-accent tw-w-6 tw-h-6 tw-flex tw-flex-col tw-items-center tw-justify-center motion-safe:tw-transition-colors ${FOCUS_RING_INSET}`}
                        >
                            <ChevronDown aria-hidden="true" className={`motion-safe:tw-transition-transform ${collapse ? '' : 'tw-rotate-180'}`} />
                        </button>
                    </div>
                </div>

                {/*
                    One wrapper for the four collapsible sections, so `aria-controls`
                    above has something to point at. They were four sibling
                    `{!collapse ? ... : null}` blocks — same mount/unmount behaviour,
                    nothing for the chevron to name.
                */}
                {!collapse ?
                    <div id={`${id}-body`}>
                        <ul id={`${id}-fields-section`}>
                            {submodel?.data?.fields.length > 0 ? submodel.data.fields.map((field, fieldIndex) => {
                                const search = searchInput?.toLowerCase();
                                const show = searchInput ? submodel.data.name.toLowerCase().includes(search) || field.name.toLowerCase().includes(search) ||
                                    (field.rules ?? []).some(rule => rule.name?.toLowerCase().includes(search)) : true;
                                if(!show) {
                                    // `return null`, not `return <></>`: a keyless fragment inside
                                    // a .map() is a child that cannot take the key, so React warned
                                    // on every filtered render. Same fix DefineModel.tsx already
                                    // carries for its own list.
                                    return null;
                                }
                                return <li id={`${id}-field-${fieldIndex}`} key={field.name + field.type + fieldIndex} className='tw-mx-2 tw-my-2'>

                                    <FieldItem
                                        id={`${id}-field-item-${fieldIndex}`}
                                        fieldName={field.name}
                                        fieldId={field?.id}
                                        fieldType={field.type}
                                        showCalculatedField={field?.dataConfiguration?.enableCalculation}
                                        selectedItem={selectedItem}
                                        classColor={field?.dataConfiguration?.class?.colour}
                                        indexInfo={indexInfo}
                                        userProjectDataModels={userProjectDataModels}
                                        onDelete={(fieldId) => setDeleteFieldId(fieldId)}
                                        onSelect={() => {
                                            handleFieldSelect(submodelIndex, fieldIndex);
                                            const selectedField = userProjectDataModels.project.dataModels[submodelIndex].data.fields[fieldIndex];
                                            setSelectedItem(selectedField);
                                            setSubSelectedItem(null)
                                        }}
                                        isSelected={field === selectedItem}
                                    />
                                </li>;
                            }) : null}
                        </ul>

                        <ul id={`${id}-categories-section`}>
                            {submodel?.data?.productCategories.length > 0 ? submodel.data.productCategories.map((category, fieldIndex) => {
                                const show = searchInput ? submodel.data.name.toLowerCase().includes(searchInput.toLowerCase()) || category?.data?.name.toLowerCase().includes(searchInput?.toLowerCase()) : true;
                                if(!show) {
                                    return null;
                                }
                                return <li id={`${id}-category-${fieldIndex}`} key={category?.data?.name + category?.type} className='tw-mx-2 tw-my-2'>
                                    <ProductCategoryItem
                                        id={`${id}-category-item-${fieldIndex}`}
                                        field={category}
                                        fieldName={category?.data?.name}
                                        fieldType={category.type}
                                        indexInfo={indexInfo}
                                        userProjectDataModels={userProjectDataModels}
                                        onDelete={deleteSubmodelCategories}
                                        selectedItem={selectedItem}
                                        onSelect={() => {
                                            handleCategorySelect(submodelIndex, fieldIndex);
                                            setSelectedItem(category);
                                            setSubSelectedItem(null)
                                        }}
                                        isSelected={category === selectedItem}
                                        setIndex={setIndex}
                                        setSubSelectedItem={setSubSelectedItem}
                                        setUserProjectDataModels={setUserProjectDataModels}
                                        onSubDelete={deleteSubmodelTransformations}
                                    />
                                </li>;
                            }) : null}
                        </ul>

                        <ul id={`${id}-connectors-section`}>
                            {submodel?.data?.connectors && submodel?.data?.connectors.length > 0 ? submodel.data.connectors.map((connector, connectorIndex) => {
                                const show = searchInput ? submodel.data.name.toLowerCase().includes(searchInput.toLowerCase()) || connector?.data?.name.toLowerCase().includes(searchInput?.toLowerCase()) : true;
                                if(!show) {
                                    return null;
                                }
                                return <li id={`${id}-connector-${connectorIndex}`} key={connector?.data?.name + connector?.type} className='tw-mx-2 tw-my-2'>
                                    <FieldItem
                                        id={`${id}-connector-item-${connectorIndex}`}
                                        fieldName={connector?.data?.name}
                                        fieldId={connector?.id}
                                        fieldType={connector.type}
                                        indexInfo={indexInfo}
                                        userProjectDataModels={userProjectDataModels}
                                        onDelete={deleteSubmodelConnectors}
                                        selectedItem={selectedItem}
                                        onSelect={() => {
                                            handleConnectorSelect(submodelIndex, connectorIndex);
                                            setSelectedItem(connector);
                                            setSubSelectedItem(null)
                                        }}
                                        isSelected={connector === selectedItem}
                                    />
                                </li>;
                            }) : null}
                        </ul>

                        {/*
                            DS Button at `sm` — 28px, up from `tw-h-6`. Measured, those
                            24px buttons held 14px text at a 21px line-height plus 8px of
                            declared padding: 29px of content in a 24px box, so the label
                            overflowed its own button by 5px. 24px was also exactly WCAG
                            2.5.8's floor.

                            Plain DS `secondary` since 2026-09-07 (BLUE_OUTLINE closed):
                            these adders repeat per card, so they stay quiet — accent
                            belongs to the selected row.
                        */}
                        <div id={`${id}-add-buttons`} className='tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-py-2 tw-px-2'>
                            <Button
                                data-testid={`${id}-add-field-btn`}
                                id={`${id}-add-field-btn`}
                                variant="secondary"
                                size="sm"
                                icon={<AddIcon color="currentColor"/>}
                                iconSize="small"
                                onClick={openFieldDialog}
                            >
                                {FIELD}
                            </Button>
                            <Button
                                data-testid={`${id}-add-category-btn`}
                                id={`${id}-add-category-btn`}
                                variant="secondary"
                                size="sm"
                                icon={<AddIcon color="currentColor"/>}
                                iconSize="small"
                                onClick={openCategoryDialog}
                            >
                                {PRODUCT_CATEGORY}
                            </Button>
                            {isConfigureDisabled ? (
                                /*
                                    `aria-disabled`, NOT `disabled` — deliberately kept from the
                                    old markup. A really-disabled button takes no focus, and the
                                    tooltip explaining WHY it is off would then be mouse-only.
                                    DS Tooltip opens on focus as well as hover, so this is the
                                    one shape where the explanation is reachable.

                                    The old greys were not grey: `tw-text-gray-400` and
                                    `tw-border-gray-300` resolve, in THIS config, to
                                    `rgba(0,0,0,0.75)` and `#091e42` — measured. So the muted
                                    button rendered as 40%-opacity near-black on a near-black
                                    border, while the icon beside it got the literal `#9ca3af`
                                    the author meant. Same failure as `tw-transition-width`: a
                                    real class that does not mean what it says.
                                */
                                <Tooltip content="Available with Configure" placement="top">
                                    <Button
                                        data-testid={`${id}-add-connector-btn-disabled`}
                                        id={`${id}-add-connector-btn`}
                                        variant="secondary"
                                        size="sm"
                                        aria-disabled="true"
                                        className='!tw-border-edge !tw-text-ink-disabled tw-cursor-not-allowed'
                                        icon={<AddIcon color="currentColor"/>}
                                        iconSize="small"
                                        onClick={(e) => e.preventDefault()}
                                    >
                                        {CONNECTOR}
                                    </Button>
                                </Tooltip>
                            ) : (
                                <Button
                                    data-testid={`${id}-add-connector-btn`}
                                    id={`${id}-add-connector-btn`}
                                    variant="secondary"
                                    size="sm"
                                    icon={<AddIcon color="currentColor"/>}
                                    iconSize="small"
                                    onClick={openConnectorDialog}
                                >
                                    {CONNECTOR}
                                </Button>
                            )}
                        </div>
                    </div>
                : null}
            </div>
            {showFieldDialog ?
                <CreateFieldDialog
                    id={`${id}-field-dialog`}
                    onClose={closeFieldDialog}
                    onFieldSubmit={onFieldSubmit}
                    multiCreate
                    entityName="Field"
                /> : null}
            {showCategoryDialog ?
                <CreateISRDialog
                    id={`${id}-category-dialog`}
                    dialogHeader={CREATE_CATEGORY}
                    labelName={CATEGORY_NAME}
                    onClose={closeCategoryDialog}
                    onSubmodelFieldSubmit={onCategoryFieldSubmit}
                    multiCreate
                    entityName="Category"
                /> : null}
            {showConnectorDialog ?
                <CreateISRDialog
                    id={`${id}-connector-dialog`}
                    dialogHeader={CREATE_CONNECTOR}
                    labelName={CONNECTOR_NAME}
                    onClose={closeConnectorDialog}
                    onSubmodelFieldSubmit={onConnectorFieldSubmit}
                    multiCreate
                    entityName="Connector"
                /> : null}  
            {deleteFieldId != null ? 
                <ConfirmDialog 
                    id={`${id}-delete-confirm-dialog`}
                    title={'Delete field'}
                    message={'This field may have other dependencies , are you sure you want to continue ?'}
                    onClose={() => {
                        setDeleteFieldId(null);
                    }} 
                    onConfirm={() => {
                        deleteSubmodelFields(deleteFieldId)
                        setDeleteFieldId(null);
                    }}
                /> : null}
            <DSConfirmDialog
                open={showDeleteSubmodelConfirm}
                tone="danger"
                title={DELETE_SUBMODEL_TITLE}
                message={DELETE_SUBMODEL_MESSAGE}
                confirmText={DELETE}
                onConfirm={confirmDeleteSubmodel}
                onCancel={() => setShowDeleteSubmodelConfirm(false)}
                data-testid={`${id}-delete-submodel-dialog`}
            />
        </React.Fragment>
    );
}

export default SubModelItem;
